import { useState, useRef, useEffect } from 'react';
import { Bell, ChevronDown, Search, LogOut, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Avatar from '../ui/Avatar';
import IconButton from '../ui/IconButton';
import ProductSwitcher from './ProductSwitcher';
import { useAuth } from '../../contexts/AuthContext';

export default function TopBar({
  user,
  currentShop,
  shops,
  onShopChange,
  onRefresh,
  notifications = 0,
  children,
}) {
  return (
    <header className="h-16 bg-[var(--color-cream-50)] border-b border-[var(--color-warm-100)] sticky top-0 z-30 flex items-center px-6 gap-4">
      {/* Product switcher — Reconciliation Hub ↔ Month-End Close */}
      <ProductSwitcher />

      {/* Shop selector */}
      {shops && onShopChange && (
        <ShopSelector shops={shops} current={currentShop} onChange={onShopChange} />
      )}

      {/* Search */}
      <div className="flex-1 max-w-md hidden md:block">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-warm-500)]" />
          <input
            placeholder="Search invoices, ROs, vendors..."
            className="w-full h-9 bg-[var(--color-cream-100)] border border-transparent hover:border-[var(--color-warm-200)] focus:bg-[var(--color-cream-50)] focus:border-[var(--color-insight-500)] focus:ring-2 focus:ring-[var(--color-insight-100)] pl-9 pr-3 rounded-[var(--radius-md)] text-[13px] text-[var(--color-warm-900)] placeholder:text-[var(--color-warm-500)] focus:outline-none transition-colors"
          />
        </div>
      </div>

      {/* Spacer */}
      <div className="flex-1 md:hidden" />

      {/* Children (page-specific actions) */}
      {children}

      {/* Notifications */}
      <div className="relative">
        <IconButton label="Notifications" variant="ghost">
          <Bell />
        </IconButton>
        {notifications > 0 && (
          <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--color-danger-500)] text-white text-[10px] font-bold flex items-center justify-center tabular-nums pointer-events-none">
            {notifications > 9 ? '9+' : notifications}
          </span>
        )}
      </div>

      {/* User menu */}
      {user && <UserMenu user={user} />}
    </header>
  );
}

function ShopSelector({ shops, current, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const currentShop = shops?.find((s) => s.shop_id === current || s.id === current);
  // API returns `shop_nickname` / `shop_name` (not `nickname`/`name`). Keep
  // both forms so the selector works regardless of the upstream shape.
  const label =
    currentShop?.shop_nickname || currentShop?.nickname ||
    currentShop?.shop_name || currentShop?.name ||
    'Select shop';

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="h-9 px-3 bg-[var(--color-cream-100)] hover:bg-[var(--color-cream-200)] border border-[var(--color-warm-100)] rounded-[var(--radius-md)] flex items-center gap-2 text-[13px] font-semibold text-[var(--color-warm-800)] transition-colors"
      >
        <span className="text-[var(--color-warm-500)] font-normal">Shop</span>
        <span className="truncate max-w-[140px]">{label}</span>
        <ChevronDown size={14} className={`text-[var(--color-warm-500)] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] shadow-[var(--shadow-lg)] overflow-hidden z-40 animate-slide-up">
          <div className="p-2 border-b border-[var(--color-warm-100)]">
            <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--color-warm-500)] px-2 py-1">
              Switch Shop
            </div>
          </div>
          <ul className="py-1 max-h-80 overflow-y-auto">
            {shops.map((shop) => (
              <li key={shop.shop_id}>
                <button
                  onClick={() => { onChange(shop.shop_id); setOpen(false); }}
                  className={`w-full text-left px-3 py-2.5 text-[13px] flex items-center justify-between hover:bg-[var(--color-cream-200)] transition-colors ${
                    shop.shop_id === current ? 'bg-[var(--color-cream-100)] font-semibold text-[var(--color-warm-900)]' : 'text-[var(--color-warm-700)]'
                  }`}
                >
                  <div className="min-w-0">
                    <div className="truncate">{shop.shop_nickname || shop.nickname || shop.shop_name || shop.name}</div>
                    {shop.city && (
                      <div className="text-[11px] text-[var(--color-warm-500)] mt-0.5">{shop.city}</div>
                    )}
                  </div>
                  {shop.shop_id === current && (
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--color-recovery-500)] shrink-0 ml-2" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function UserMenu({ user }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const displayName = user.full_name || user.name || user.email || 'User';

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 h-10 pl-1 pr-2.5 rounded-[var(--radius-md)] hover:bg-[var(--color-cream-200)] transition-colors"
      >
        <Avatar name={displayName} size="sm" />
        <div className="hidden sm:block text-left">
          <div className="text-[12px] font-semibold text-[var(--color-warm-900)] leading-tight">{displayName}</div>
          <div className="text-[11px] text-[var(--color-warm-500)] leading-tight capitalize">{user.role?.replace('_', ' ')}</div>
        </div>
        <ChevronDown size={13} className="text-[var(--color-warm-500)] hidden sm:block" />
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-1 w-56 bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] shadow-[var(--shadow-lg)] overflow-hidden z-40 animate-slide-up">
          <div className="p-3 border-b border-[var(--color-warm-100)]">
            <div className="text-[13px] font-semibold text-[var(--color-warm-900)] truncate">{displayName}</div>
            <div className="text-[11px] text-[var(--color-warm-500)] truncate">{user.email}</div>
          </div>
          <ul className="py-1">
            <MenuItem
              icon={<User size={14} />}
              onClick={() => { setOpen(false); navigate('/profile'); }}
            >
              Profile
            </MenuItem>
          </ul>
          <div className="border-t border-[var(--color-warm-100)]">
            <ul className="py-1">
              <MenuItem icon={<LogOut size={14} />} danger onClick={logout}>
                Sign out
              </MenuItem>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon, children, danger, onClick }) {
  return (
    <li>
      <button
        onClick={onClick}
        className={`
          w-full text-left px-3 py-2 text-[13px] flex items-center gap-2.5
          transition-colors
          ${danger ? 'text-[var(--color-danger-600)] hover:bg-[var(--color-danger-50)]' : 'text-[var(--color-warm-700)] hover:bg-[var(--color-cream-200)] hover:text-[var(--color-warm-900)]'}
        `}
      >
        {icon && <span className="text-[var(--color-warm-500)]">{icon}</span>}
        {children}
      </button>
    </li>
  );
}
